import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useOptimizedAuth } from "@/hooks/useOptimizedAuth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

interface AdminProtectedRouteProps {
  children: ReactNode;
}

const AdminProtectedRoute = ({ children }: AdminProtectedRouteProps) => {
  const { user, hasAdminAccess, loading } = useOptimizedAuth();

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Simple admin access check - if authenticated, allow access
  // The hasAdminAccess logic will be handled by the auth context
  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-semibold mb-2">Admin Access Required</div>
              <div className="text-sm">
                Please sign in with an admin account to access this panel.
              </div>
            </AlertDescription>
          </Alert>
          <div className="text-sm text-muted-foreground mb-4">
            Test admin credentials:<br />
            <code className="text-xs">superadmin@vramstore.com</code>
          </div>
          <a 
            href="/auth"
            className="text-primary hover:text-primary/80 text-sm underline mr-4"
          >
            Sign In as Admin
          </a>
          <a 
            href="/"
            className="text-muted-foreground hover:text-foreground text-sm underline"
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminProtectedRoute;